<script setup>
import { ref } from "vue"
import { SearchIcon } from "@heroicons/vue/solid";
import { storeToRefs } from 'pinia'
import { useUserStore } from '@stores/user';

import SearchModal from "../SearchModal/SearchModal.vue";
import HamburguerButton from "./HamburguerButton.vue";
import BrandSelector from "./BrandSelector.vue";
import UserIcon from "./UserIcon.vue";

const props = defineProps({
  isSidebarHidden: Boolean,
  isSidebarCollapsed: Boolean,
  isDarkModeSelected: Boolean,
  hasNotifications: {
    type: Boolean,
    default: false
  },
  isLive: {
    type: Boolean,
    default: false
  },
  showRecommendation: {
    type: Boolean,
    default: false
  },
});
const emit = defineEmits([
  "onCollapseSidebar",
  "onBrandSelect",
  "onColorModeToggle",
]);
const showSearchModal = ref(false);
const toggleSearchModal = (val) => {
  showSearchModal.value = val;
}
const userStore = useUserStore();
const { isUserAMember, userDisplayName, userDashboardUrl, userProfilePictureUrl, brand } = storeToRefs(userStore);
</script>

<template>
  <nav
    class="
      tw-z-[100]
      tw-fixed
      tw-top-0
      tw-flex
      tw-h-[58px]
      tw-w-full
      tw-justify-between
      tw-pl-1.5
      tw-pr-2
      md:tw-pr-4
      tw-transition
      tw-bg-white
      dark:tw-bg-[#081825]
      tw-shadow-[0_0_5px_rgb(0,0,0,.2)]
    "
  >
    <div class="tw-row tw-flex tw-items-center">
      <HamburguerButton
        :isSidebarHidden="isSidebarHidden"
        :isSidebarCollapsed="isSidebarCollapsed"
        @onclick="emit('onCollapseSidebar')"
      />

      <!-- Logo -->
      <a :href="`/${brand}`"
        class="tw-hidden tw-w-[140px] tw-h-full lg:tw-flex tw-text-[#00101D] dark:tw-text-white tw-mr-16"
      >
        <svg class="tw-transition" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 11878.76 2760.46">
          <path fill="currentColor" d="m10856.54,679.46c-92.03,0-179.68,7.14-261.47,20.44,53.8,33.99,99.85,74.34,135.83,119.41l2.05,2.57,1.86,2.71c2.83,4.14,5.76,8.34,8.86,12.78,9.35,13.42,19.86,28.51,30.45,45.8,181.15,45.98,280.88,191.96,280.88,420.55v294.56c0,43.34-33.17,71.5-71.76,71.5-7.67,0-15.55-1.11-23.44-3.44-108.03-31.88-237.51-54.43-376.6-54.43-430.59,0-760.1,252.12-760.1,584.28s269.59,564.27,629.06,564.27c186.96,0,375.52-46.68,502.94-153.53,20.57-17.25,45.7-27.7,69.83-27.7s45.27,9.57,61.66,31.92c.73,1.51,1.47,3.01,2.24,4.51,23.48,45.86,61.33,81.03,108.56,104.78,47.52,23.9,104.56,36.23,165.99,36.23s124.63-11.84,188.52-36.23c19.93-7.61,39.81-16.44,59.48-26.51,155.36-79.54,255.06-214.42,266.03-340.42.79-7.85,1.35-893.68,1.35-893.68,0-444.21-430.6-780.38-1022.22-780.38Zm198.46,1602.83c0,10.31-3.86,20.41-10.8,29.18-44.11,55.78-103.9,85.99-176.42,85.99-123.57,0-205.93-106.54-205.93-260.43,0-174.6,104.84-278.18,250.87-278.18,42.22,0,82.31,8.36,113.94,25.08,18.32,9.68,28.35,26.48,28.35,43.75v354.6Z"/>
          <path fill="currentColor" d="m10754.56,1113.46h0c-11-128-57-184-94-238-95-119-276.74-197-460.85-197-137.58,0-285.61,40.34-386.15,133-24.98,23.03-48.6,35.62-66.36,35.62-5.3,0-10.07-1.12-14.2-3.42-12-7-16-14-25.82-28.16-28.76-41.48-54.54-74.52-95.87-98-49.91-28.35-111.68-43.02-178.68-43.02-78.98,0-165.24,20.37-248,62.75-34.66,17.75-66.56,38.25-95.31,60.79-100.14,78.5-162.21,181.76-170.72,279.66.26.37.52.73.78,1.1,42.35,60.37,78.64,125.34,107.86,193.09,29.64,68.72,52.36,141.15,67.55,215.27,15.47,75.53,23.31,153.86,23.31,232.82s-7.84,157.29-23.31,232.82c-15.18,74.12-37.91,146.55-67.55,215.27-29.22,67.75-65.51,132.72-107.86,193.09-.41.58-.83,1.16-1.24,1.74.27,47.52.83,150.89.72,157.89,1.61,32.74,9.65,64.43,24.73,93.88,35.34,69.04,103.27,113.87,186.73,131.95,27.77,6.01,57.25,9.07,87.83,9.07s61.6-2.99,93.4-9.07c51.18-9.78,103.55-27.54,154.6-53.68,160.45-82.14,261.54-223.31,266.83-352.76,0,0-2-912.78-2.04-920.16-1.36-271.54,249.25-505.25,453.98-506.33,2.14-.01,4.23-.02,6.27-.02,36.77,0,58.47,1.86,90.38,6.8-30.5,9.67-44.25,20.53-74.35,35.94-211.13,108.09-319.49,318.38-242.02,469.7,47.1,92,152.05,141.02,274.54,141.02,78.98,0,165.24-20.37,248.01-62.75,158.74-81.27,259.38-220.31,266.62-348.63.62-18.08.62-27.08.19-42.28Z"/>
          <path fill="currentColor" d="m6172.31,2759.46c543.41,0,834.64-272.13,834.64-672.32,0-464.22-419.09-616.3-692.57-756.36-134.31-65.97-231.88-109.94-296.78-158.98-52.96-41.8-76.36-73.42-73.36-114.21,7.33-99.95,129.2-144.77,307.41-144.78h.02c28.97,0,59.4,1.18,91.09,3.51,36.21,2.65,72.8,6.14,112.8,13.14,59.19,11.66,88.62,26.54,120.03,50.4-.37.19-.75.37-1.12.56-130.78,66.95-197.9,197.22-149.91,290.95,29.17,56.99,94.19,87.35,170.06,87.35,48.92,0,102.36-12.62,153.63-38.87,92.56-47.39,152.77-120.98,163.32-196.39,0,0,3.06-33.91,3.06-48.81-3.55-224.11-316.1-396.19-724.54-396.19-500.78,0-820.43,252.12-820.43,660.32,0,484.23,479.48,652.31,689.03,768.37,148.25,82.47,251.88,142.59,316.65,197.48,19.26,24.36,27.6,48.08,25.74,73.42-7.33,99.95-129.2,144.78-307.41,144.78-28.97,0-59.41-1.18-91.11-3.51-60.14-4.41-117.81-9.22-170.04-22.89-41.94-10.97-76.94-21.97-121.94-47.97.27-.41.55-.81.83-1.21,130.02-67.13,196.61-196.89,148.76-290.34-29.17-56.99-94.19-87.35-170.06-87.35-48.92,0-102.36,12.62-153.63,38.87-115.2,58.98-180.99,167.08-162.23,256.06,42.12,222.66,368.59,394.97,798.07,394.97Z"/>
          <path fill="currentColor" d="m7925.76,679.46c-560.65,0-996.75,358.25-1082.89,857.4,8.01,6.97,15.84,13.99,23.44,21.07,34.32,32,65.14,65.95,91.61,100.92,28.98,38.29,53.74,79.12,73.6,121.34,21.23,45.14,37.5,93.22,48.35,142.92,11.34,51.93,17.09,107.11,17.09,164.02,0,98.68-15.98,191.14-47.53,275.4,197.12,245.09,510.16,397.92,876.34,397.92,631,0,1102.33-448.22,1102.33-1040.5s-471.32-1040.5-1102.33-1040.5Zm-2.21,1846c-177.11,0-268.49-294.62-268.49-806.5s95.38-805.5,272.49-805.5,272.81,293.62,272.81,805.5-99.7,806.5-276.81,806.5Z"/>
          <path fill="currentColor" d="m2603.55,2615.66c47.1,92,152.06,141.02,274.54,141.02,78.98,0,165.24-20.37,248.01-62.75,211.13-108.09,316.25-316.77,242.02-469.7-5.13-10.56-1008.13-2072.56-1008.13-2072.56,0,0-4.16-9.42-4.79-10.65C2308.1,49.02,2203.15,0,2080.66,0c-78.98,0-165.24,20.37-248.01,62.75-195.28,99.98-278.65,280.92-256.35,434.69l154.25,1056.08-339.5-698.44-3.06-6.41c-6-13-7.82-19.41-14.34-32.16-47.1-92-152.05-141.02-274.54-141.02-78.98,0-165.24,20.37-248.01,62.75-172.92,88.52-276.89,245.6-266.31,382.69h-.08l-2.44,967.49c-21.6-3.45-44.1-5.2-67.22-5.2-78.98,0-165.24,20.37-248.01,62.75-211.13,108.09-319.49,318.38-242.02,469.7,47.1,92,152.05,141.02,274.54,141.02,78.98,0,165.24-20.37,248.01-62.75,168.29-86.16,271.28-237.26,266.91-371.66l1.73-685.02,438.77,915.42c16,32,28.56,51.87,34.26,62.99,47.1,92,152.05,141.02,274.54,141.02,78.98,0,165.24-20.37,248.01-62.75,193.14-98.88,283.19-281.26,257.82-429.83l-9.63-67.43-147.2-1006.35,672.2,1393.35c7,13,13.36,21.84,18.55,31.99Z"/>
          <path fill="currentColor" d="m5258.28,815.5c-21.33-41.67-54.53-74.51-95.85-97.98-49.91-28.36-111.68-43.03-178.7-43.03-78.98,0-165.24,20.38-248,62.75-33.57,17.19-64.54,36.96-92.58,58.67-112.87,87.38-178.21,206.12-174.29,313.97.13,12.8-.24,947.77-.24,947.77,0,9.04-1.52,18.1-4.6,26.54-56.07,153.16-143.66,259.22-227.25,259.22-74.71,0-139.47-80.02-139.47-188.05,0,0-1.32-1221.67-1.06-1250.83-2.14-31-10.1-61.01-24.43-89-21.23-41.46-54.2-74.19-95.23-97.64-50.02-28.58-112.03-43.38-179.32-43.38-78.98,0-165.24,20.37-248.01,62.75-33.85,17.33-65.06,37.29-93.28,59.21-98.33,76.37-160.42,176.56-171.84,272.49-.09,84.75-.3,482.28-.45,762.13,0,0,176.78,362.76,177.83,364.81,12.06,23.55,20.79,48.75,25.96,74.91,4.97,25.17,6.68,51.43,5.08,78.06-3.05,50.87-17.82,102.32-43.9,152.92-6.73,13.06-14.15,25.89-22.2,38.49,110.46,137.09,275.21,219.2,459.19,219.2s338.79-74.17,462.73-196.74c16.04-15.86,36.33-24.84,56.79-24.84,30.85,0,62.1,20.4,79.79,68.37,1.63,2.98,3.37,5.91,5.18,8.8,31.25,57.86,85.81,98.17,153.2,119.33,39.54,14.08,83.95,21.29,130.92,21.29,60.19,0,124.6-11.83,188.48-36.22,0,0,33.55-14.71,53.23-24.79,168.2-86.11,271.45-238.82,267.2-373.17h0s.08,0,.12,0c.02,0,.05,0,.07,0V937.01c-.39-17.34-3.34-79-25.1-121.51Z"/>
        </svg>
      </a>

      <BrandSelector
        @onBrandSelect="emit('onBrandSelect', $event)"
        :brand="brand"
      />
    </div>
    <div class="tw-self-end tw-h-full tw-flex tw-items-center tw-ml-auto">

      <!-- Live Indicator -->
      <a v-if="isLive" :href="`/${brand}/live`" class="tw-relative tw-mr-2">
        <span
          class="tw-animate-grow-shrink tw-blur-sm tw-origin-center tw-absolute tw-left-0 tw-top-0 tw-inline-flex tw-h-full tw-w-full tw-rounded tw-bg-[#F71B26] tw-opacity-75"></span>
        <span
          class="tw-relative tw-whitespace-nowrap tw-base tw-pt-[2px] tw-text-sm tw-pb-0 tw-px-[3px] tw-rounded tw-leading-none tw-text-white tw-bg-[#DC2626] tw-uppercase tw-font-bebas-neue">On-Air</span>
      </a>

      <!-- Search Trigger -->
      <button
        @click="() => toggleSearchModal(true)"
        class="tw-shrink-0 lg:tw-hidden tw-w-[40px] sm:tw-w-[58px] tw-h-full tw-flex tw-items-center tw-mr-1 tw-justify-center hover:dark:tw-bg-[#102230] hover:tw-bg-[#F5F5F6]"
        title="Musora Search"
      >
        <SearchIcon class="tw-w-[24px] tw-h-[24px] tw-text-[#00101D] dark:tw-text-white" />
      </button>

      <!-- Invite a Friend -->
      <a v-if="isUserAMember"
        :href="`/${ brand }/referral/invite-a-friend`"
        class="tw-shrink-0 tw-w-[40px] sm:tw-w-[58px] tw-h-full tw-flex tw-items-center tw-mr-1 tw-justify-center hover:dark:tw-bg-[#102230] hover:tw-bg-[#F5F5F6]"
        title="Invite a Friend"
      >
        <span class="tw-text-[#00101D] dark:tw-text-white">
          <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.5 7V21.625M11.5 7C11.5 7 11.5 5.32063 11.5 4.75C11.5 3.50736 12.5074 2.5 13.75 2.5C14.9926 2.5 16 3.50736 16 4.75C16 5.99264 14.9926 7 13.75 7C13.078 7 11.5 7 11.5 7ZM11.5 7C11.5 7 11.5 4.82078 11.5 4.1875C11.5 2.6342 10.2408 1.375 8.6875 1.375C7.1342 1.375 5.875 2.6342 5.875 4.1875C5.875 5.7408 7.1342 7 8.6875 7C9.60752 7 11.5 7 11.5 7ZM3.625 11.5H19.375M3.625 11.5C2.38236 11.5 1.375 10.4926 1.375 9.25C1.375 8.00736 2.38236 7 3.625 7H19.375C20.6176 7 21.625 8.00736 21.625 9.25C21.625 10.4926 20.6176 11.5 19.375 11.5M3.625 11.5L3.625 19.375C3.625 20.6176 4.63236 21.625 5.875 21.625H17.125C18.3676 21.625 19.375 20.6176 19.375 19.375V11.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
      </a>

      <UserIcon
        :brand="brand"
        :userAvatar="userProfilePictureUrl"
        :user-name="userDisplayName"
        :account-url="userDashboardUrl"
        :has-notifications="hasNotifications"
        :isDarkModeSelected="isDarkModeSelected"
        :show-recommendation="showRecommendation"
        @onColorModeToggle="emit('onColorModeToggle')"
      />
    </div>

    <SearchModal
        v-if="showSearchModal"
        @onClose="() => toggleSearchModal(false)"
        :brand="brand"
    />
  </nav>
</template>
